import { Box, HStack, VStack, SimpleGrid } from "@chakra-ui/react";
import { Card } from "@chakra-ui/react/card";

interface CollectionLoadingSkeletonProps {
  variant?: 'mobile' | 'desktop';
}

export default function CollectionLoadingSkeleton({ variant = 'mobile' }: CollectionLoadingSkeletonProps) {
  if (variant === 'desktop') {
    return (
      <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
        {/* Header panel */}
        <Box borderBottomWidth="1px" borderColor="border.muted" bg="bg.canvas" px={8} pt={6} pb={0} flexShrink={0}>
          <Box display="flex" gap={4} alignItems="flex-start">
            <Box flex={1} minW={0}>
              <Box h="9px" w="70px" bg="gray.200" borderRadius="sm" mb={3} className="skeleton-pulse" />
              <Box h="38px" w="280px" bg="gray.200" borderRadius="md" mb={3} className="skeleton-pulse" />
              <Box h="20px" w="72px" bg="gray.200" borderRadius="full" className="skeleton-pulse" />
            </Box>
            <HStack gap={2} pt={1} flexShrink={0}>
              <Box h="30px" w="74px" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
              <Box h="30px" w="64px" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
              <Box w="1px" h="18px" bg="border.muted" mx={1} />
              <Box h="34px" w="118px" bg="gray.300" borderRadius="lg" className="skeleton-pulse" />
            </HStack>
          </Box>
          <Box borderTopWidth="1px" borderColor="border.muted" mt={4} py={3} mx={-8} px={8} bg="bg.surface" />
        </Box>

        {/* Split body */}
        <Box flex={1} display="flex" overflow="hidden" minH={0}>
          {/* Left: recipe grid */}
          <Box flex={1} overflowY="auto" px={6} pt={5} pb={8} borderRightWidth="1px" borderColor="border.muted">
            <Box h="8px" w="56px" bg="gray.200" borderRadius="sm" mb={4} className="skeleton-pulse" />
            <SimpleGrid columns={2} gap={4}>
              {[1, 2, 3, 4].map((i) => (
                <Card.Root key={i}>
                  <Card.Body p={3}>
                    <HStack gap={3}>
                      <Box h="80px" w="90px" bg="gray.200" borderRadius="md" flexShrink={0} className="skeleton-pulse" />
                      <VStack gap={2} align="start" flex={1}>
                        <Box h="15px" w="85%" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
                        <Box h="13px" w="60%" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
                        <Box h="13px" w="70%" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
                      </VStack>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
          </Box>

          {/* Right: shopping list sidebar */}
          <Box w="320px" flexShrink={0} bg="bg.surface" px={4} pt={5} pb={8}>
            <Box h="8px" w="96px" bg="gray.200" borderRadius="sm" mb={4} className="skeleton-pulse" />
            <VStack gap={1} align="stretch">
              {([75, 55, 90, 65, 80, 50] as const).map((width, i) => (
                <Box key={i} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
                  <HStack gap={2}>
                    <Box h="16px" w="16px" bg="gray.200" borderRadius="sm" flexShrink={0} className="skeleton-pulse" />
                    <Box h="13px" w={`${width}%`} bg="gray.200" borderRadius="sm" className="skeleton-pulse" />
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <VStack gap={0} align="stretch">
      {/* Hero strip placeholder */}
      <Box h="200px" bg="gray.200" className="skeleton-pulse" mx={-4} />

      {/* Tabs placeholder */}
      <HStack gap={6} px={1} pt={2} pb={0} borderBottomWidth="1px" borderColor="border.muted">
        <Box h="36px" w="90px" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
        <Box h="36px" w="110px" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
      </HStack>

      {/* Horizontal recipe card skeletons */}
      <VStack gap={3} align="stretch" pt={4}>
        {[1, 2, 3, 4].map((i) => (
          <Card.Root key={i}>
            <Card.Body p={3}>
              <HStack gap={3}>
                <Box h="80px" w="90px" bg="gray.200" borderRadius="md" flexShrink={0} className="skeleton-pulse" />
                <VStack gap={2} align="start" flex={1}>
                  <Box h="16px" w="75%" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
                  <Box h="13px" w="50%" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
                  <Box h="13px" w="60%" bg="gray.200" borderRadius="md" className="skeleton-pulse" />
                </VStack>
              </HStack>
            </Card.Body>
          </Card.Root>
        ))}
      </VStack>
    </VStack>
  );
}
